<script setup lang="ts">
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { DraftEventName } from '@/helpers/draftEvents';
import { ref } from 'vue';
import type { ExitStrategyEntry } from '@/types/ExitStrategy';
import type { ExitStrategy } from '@/api/enums';
import { exitStrategySchema } from '@/validation/schemas/v1/exit-strategy';
import { useForm } from 'vee-validate';
import { ExitStrategyOptions } from '@/helpers/const';

const { loanApp, state, saveAndGoNext, goBack } = useBrokerLoanAppProcess();
const { saveApplicationToDraft } = useApplicationApi();
const isLoading = ref(false);

const { values, handleSubmit: validateSubmit } = useForm<ExitStrategyEntry>({
  validationSchema: exitStrategySchema,
  initialValues: {
    exitStrategy: loanApp.value.exitStrategy as ExitStrategy,
    exitStrategyDetails: loanApp.value.exitStrategyDetails,
  },
  validateOnMount: false,
});

const handleSubmit = validateSubmit(async (values) => {
  isLoading.value = true;
  try {
    let applicationDraftId = loanApp.value.applicationDraftId;
    if (loanApp.value.brokerId) {
      applicationDraftId = await saveApplicationToDraft(
        state.value.context,
        {
          ...loanApp.value,
          exitStrategy: values.exitStrategy,
          exitStrategyDetails: values.exitStrategyDetails,
        },
        DraftEventName.ExitStrategy,
      );
    }
    saveAndGoNext({
      exitStrategy: values.exitStrategy,
      exitStrategyDetails: values.exitStrategyDetails,
      applicationDraftId,
    });
  } catch (error) {
    console.error('Failed to save exit strategy:', error);
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What is your client's
        <BxHighlight>loan repayment strategy</BxHighlight>
        ?
      </h2>
    </div>
    <form @submit="handleSubmit">
      <BxField v-slot="{ field }" name="exitStrategy">
        <BxSelect
          v-bind="field"
          id="exit-strategy"
          data-test-id="exit-strategy"
          label="Loan repayment strategy"
          label-type="floating"
          placeholder="Select exit strategy..."
          :list-items="ExitStrategyOptions"
          :truncate="false"
          class="form-control"
        />
      </BxField>
      <BxField v-if="values.exitStrategy === 'other'" v-slot="{ field }" name="exitStrategyDetails">
        <BxTextarea
          v-bind="field"
          id="exit-strategy-details"
          data-test-id="exit-strategy-details"
          label="Provide additional repayment strategy details"
          label-type="default"
          class="form-control"
        />
      </BxField>
      <div class="mt-6">
        <BxButton id="exit-strategy-continue" variant="primary" :loading="isLoading" @click="handleSubmit()"
          >Save and continue</BxButton
        >
        <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
      </div>
    </form>
  </div>
</template>
